import { useState } from "react";
import { Clapperboard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Section } from "@/components/editor/Section";
import { AssetList } from "@/components/editor/AssetList";
import { api, formatApiError } from "@/lib/api";

const STYLES = [["cinematic-3d", "Cinematic 3D"], ["anime-2d", "2D Anime"], ["claymation", "Claymation"], ["noir", "Noir"],
  ["watercolour", "Watercolour"], ["pixel", "Pixel art"], ["comic", "Comic cel"]];

const Pills = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`text-[11px] px-2.5 py-1 rounded transition-colors ${value === k ? "bg-[#00E5FF] text-[#0B0C10]" : "bg-[#0B0C10] border border-[#2A2E3D] text-slate-300 hover:border-[#00E5FF]"}`}>{l}</button>
    ))}
  </div>
);

export const AnimationPanel = ({ projectId, assets, onCreated, onAdd, onView }) => {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("cinematic-3d");
  const [duration, setDuration] = useState("5");
  const [aspect, setAspect] = useState("16:9");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/animation", { project_id: projectId, prompt, style, duration, aspect_ratio: aspect });
      onCreated(data); toast.success("Animation is rendering — usually 1-3 minutes");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="animation-panel">
      <Section title="Generate animation">
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. A paper boat sails through a neon-lit flooded city at night, camera slowly tracking"
          className="bg-[#0B0C10] text-sm min-h-[84px]" data-testid="animation-prompt-input" />
        <p className="text-[11px] uppercase font-mono-ui tracking-wider text-slate-500 mt-3 mb-2">Style</p>
        <Pills options={STYLES} value={style} onChange={setStyle} prefix="animation-style" />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><p className="text-[11px] uppercase font-mono-ui tracking-wider text-slate-500 mb-2">Length</p>
            <Pills options={[["5", "5 s"], ["10", "10 s"]]} value={duration} onChange={setDuration} prefix="animation-duration" /></div>
          <div><p className="text-[11px] uppercase font-mono-ui tracking-wider text-slate-500 mb-2">Aspect</p>
            <Pills options={[["16:9", "16:9"], ["9:16", "9:16"], ["1:1", "1:1"]]} value={aspect} onChange={setAspect} prefix="animation-aspect" /></div>
        </div>
        <Button onClick={generate} disabled={busy || prompt.trim().length < 2} className="w-full mt-4 bg-[#00E5FF] hover:bg-[#5cefff] text-[#0B0C10]" data-testid="animation-generate-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clapperboard className="w-4 h-4 mr-2" />}Generate animation
        </Button>
      </Section>
      <Section title="Your animations">
        <AssetList assets={assets.filter((a) => a.kind === "animation")} empty="Rendered animations appear here." onAdd={onAdd} onView={onView} />
      </Section>
    </div>
  );
};
